import React, { FC, useMemo } from 'react';
import { Grid, Header, Message } from 'semantic-ui-react';
import { ErrorMessage, Field } from 'formik';
import { EMisureEControlli } from '@relab/alstom-ptw-shared';
import { DatePicker } from '../../../../DatePicker';
import { InputWithoutEnter } from '../../../../InputWithoutEnter';
import { BorderedDiv } from '../../StyledEhsComponents';
import { TranslationComponent } from '../../../../TranslationComponent';
import { useTranslation } from 'react-i18next';
import { InputLabel } from '../../../../styled/InputLabel.styled';
import { SicurezzaAntincendio } from './SicurezzaAntincendio';

type Props = {
  showFirePrevention?: boolean;
  showConfinedSpaces?: boolean;
};

export const MisureEControlliComponent: FC<Props> = ({ showFirePrevention, showConfinedSpaces }) => {
  const { t } = useTranslation();

  const filteredMisureEControlli = useMemo(() => {
    return (Object.keys(EMisureEControlli) as EMisureEControlli[]).filter((measure) =>
      measure === EMisureEControlli.presenzaTreppiede
        ? showConfinedSpaces
        : ![
            EMisureEControlli.dpiIii,
            EMisureEControlli.sollevamento,
            EMisureEControlli.confinati,
            EMisureEControlli.taglio,
          ].includes(measure)
    );
  }, [showConfinedSpaces]);

  const renderMisureEControlliExtra = (misure: EMisureEControlli) => {
    switch (misure) {
      case EMisureEControlli.elettrico:
        return (
          <Grid>
            <Grid.Row verticalAlign={'middle'}>
              <Grid.Column width={1} />
              <Grid.Column width={4}>
                <InputLabel>
                  <Field type="checkbox" name="elettricoTensioneAssente" />
                  <TranslationComponent stringKey={'Controllata l’assenza di tensione. Nessuna tensione rilevata'} />
                </InputLabel>
              </Grid.Column>
            </Grid.Row>
          </Grid>
        );

      case EMisureEControlli.idraulico:
        return (
          <Grid>
            <Grid.Row verticalAlign={'middle'}>
              <Grid.Column width={1} />
              <Grid.Column width={4}>
                <InputLabel>
                  <Field type="checkbox" name="idraulicoTensioneAssente" />
                  <TranslationComponent stringKey={'Controllata l’assenza di pressione. Nessuna pressione rilevata.'} />
                </InputLabel>
              </Grid.Column>
            </Grid.Row>
          </Grid>
        );

      case EMisureEControlli.pneumatico:
        return (
          <Grid>
            <Grid.Row verticalAlign={'middle'}>
              <Grid.Column width={1} />
              <Grid.Column width={4}>
                <InputLabel>
                  <Field type="checkbox" name="pneumaticoTensioneAssente" />
                  <TranslationComponent
                    stringKey={
                      'Controllata l’assenza di pressione e temperatura. Nessuna pressione e temperatura rilevata.'
                    }
                  />
                </InputLabel>
              </Grid.Column>
            </Grid.Row>
          </Grid>
        );

      case EMisureEControlli.ossigeno:
        return (
          <Grid>
            <Grid.Row verticalAlign={'middle'}>
              <Grid.Column width={3}>
                <InputWithoutEnter
                  name={'ossigenoValorePrima'}
                  label={t('Valore letto prima')}
                  inputProps={{ type: 'number' }}
                />
              </Grid.Column>
              <Grid.Column width={3}>
                <InputWithoutEnter
                  name={'ossigenoValoreDurante'}
                  label={t('Valore letto durante')}
                  inputProps={{ type: 'number' }}
                />
              </Grid.Column>
              <Grid.Column width={3}>
                <InputWithoutEnter
                  name={'ossigenoValoreTermine'}
                  label={t('Valore letto al termine')}
                  inputProps={{ type: 'number' }}
                />
              </Grid.Column>
            </Grid.Row>
          </Grid>
        );

      case EMisureEControlli.monossido:
        return (
          <Grid>
            <Grid.Row verticalAlign={'middle'}>
              <Grid.Column width={3}>
                <InputWithoutEnter
                  name={'monossidoValorePrima'}
                  label={t('Valore letto prima')}
                  inputProps={{ type: 'number' }}
                />
              </Grid.Column>
              <Grid.Column width={3}>
                <InputWithoutEnter
                  name={'monossidoValoreDurante'}
                  label={t('Valore letto durante')}
                  inputProps={{ type: 'number' }}
                />
              </Grid.Column>
              <Grid.Column width={3}>
                <InputWithoutEnter
                  name={'monossidoValoreTermine'}
                  label={t('Valore letto al termine')}
                  inputProps={{ type: 'number' }}
                />
              </Grid.Column>
            </Grid.Row>
          </Grid>
        );

      case EMisureEControlli.metano:
        return (
          <Grid>
            <Grid.Row verticalAlign={'middle'}>
              <Grid.Column width={3}>
                <InputWithoutEnter
                  name={'metanoValorePrima'}
                  label={t('Valore letto prima')}
                  inputProps={{ type: 'number' }}
                />
              </Grid.Column>
              <Grid.Column width={3}>
                <InputWithoutEnter
                  name={'metanoValoreDurante'}
                  label={t('Valore letto durante')}
                  inputProps={{ type: 'number' }}
                />
              </Grid.Column>
              <Grid.Column width={3}>
                <InputWithoutEnter
                  name={'metanoValoreTermine'}
                  label={t('Valore letto al termine')}
                  inputProps={{ type: 'number' }}
                />
              </Grid.Column>
            </Grid.Row>
          </Grid>
        );

      case EMisureEControlli.idrogeno:
        return (
          <Grid>
            <Grid.Row verticalAlign={'middle'}>
              <Grid.Column width={3}>
                <InputWithoutEnter
                  name={'idrogenoValorePrima'}
                  label={t('Valore letto prima')}
                  inputProps={{ type: 'number' }}
                />
              </Grid.Column>
              <Grid.Column width={3}>
                <InputWithoutEnter
                  name={'idrogenoValoreDurante'}
                  label={t('Valore letto durante')}
                  inputProps={{ type: 'number' }}
                />
              </Grid.Column>
              <Grid.Column width={3}>
                <InputWithoutEnter
                  name={'idrogenoValoreTermine'}
                  label={t('Valore letto al termine')}
                  inputProps={{ type: 'number' }}
                />
              </Grid.Column>
            </Grid.Row>
          </Grid>
        );

      case EMisureEControlli.imbracature:
        return (
          <Grid>
            <Grid.Row verticalAlign={'middle'}>
              <Grid.Column width={1}>
                <InputLabel>
                  <Field type="radio" name="imbracatureSiNo" value={'true'} />
                  <TranslationComponent stringKey={'Si'} />
                </InputLabel>
              </Grid.Column>
              <Grid.Column width={1}>
                <InputLabel>
                  <Field type="radio" name="imbracatureSiNo" value={'false'} />
                  <TranslationComponent stringKey={'No'} />
                </InputLabel>
              </Grid.Column>
              <Grid.Column width={3}>
                <DatePicker label={t('Data ultimo controllo')} name="imbracatureDateLast" />
              </Grid.Column>
            </Grid.Row>
            <Grid.Row>
              <Grid.Column width={2}>
                <ErrorMessage name={'imbracatureSiNo'}>
                  {(errorMessage) => <Message error visible content={errorMessage} />}
                </ErrorMessage>
              </Grid.Column>
            </Grid.Row>
          </Grid>
        );

      case EMisureEControlli.formazioneUtilizzo:
        return (
          <Grid>
            <Grid.Row verticalAlign={'middle'}>
              <Grid.Column width={1} />
              <Grid.Column width={3}>
                <InputLabel>
                  <Field type="checkbox" name="misureEControlli" value={EMisureEControlli.dpiIii} />
                  <span>
                    <TranslationComponent stringKey={EMisureEControlli.dpiIii} />
                  </span>
                </InputLabel>
              </Grid.Column>
              <Grid.Column width={3}>
                <InputLabel>
                  <Field type="checkbox" name="misureEControlli" value={EMisureEControlli.sollevamento} />
                  <span>
                    <TranslationComponent stringKey={EMisureEControlli.sollevamento} />
                  </span>
                </InputLabel>
              </Grid.Column>
              <Grid.Column width={3}>
                <InputLabel>
                  <Field type="checkbox" name="misureEControlli" value={EMisureEControlli.confinati} />
                  <span>
                    <TranslationComponent stringKey={EMisureEControlli.confinati} />
                  </span>
                </InputLabel>
              </Grid.Column>
              <Grid.Column width={3}>
                <InputLabel>
                  <Field type="checkbox" name="misureEControlli" value={EMisureEControlli.taglio} />
                  <span>
                    <TranslationComponent stringKey={EMisureEControlli.taglio} />
                  </span>
                </InputLabel>
              </Grid.Column>
            </Grid.Row>
            <Grid.Row>
              <Grid.Column width={6}>
                <ErrorMessage name={'formazioneUtilizzo'}>
                  {(errorMessage) => <Message error visible content={errorMessage} />}
                </ErrorMessage>
              </Grid.Column>
            </Grid.Row>
          </Grid>
        );

      case EMisureEControlli.matricolaAnalizzatore:
        return showConfinedSpaces ? (
          <Grid.Row key={misure} verticalAlign={'middle'}>
            <Grid.Column width={6}>
              <InputWithoutEnter name={'matricolaAnalizzatore'} label={t('matricolaAnalizzatore')} />
            </Grid.Column>

            <Grid.Column width={3}>
              <DatePicker label={t('scadenzaCalibrazione')} name={'scadenzaCalibrazione'} />
            </Grid.Column>
          </Grid.Row>
        ) : null;

      case EMisureEControlli.dpiIii:
      case EMisureEControlli.sollevamento:
      case EMisureEControlli.confinati:
      case EMisureEControlli.taglio:
      case EMisureEControlli.scadenzaCalibrazione:
        return null;
    }
  };

  return (
    <BorderedDiv>
      <Header>
        <TranslationComponent stringKey={'Misure preventive e controlli di sicurezza'} />{' '}
      </Header>

      {showFirePrevention && <SicurezzaAntincendio />}

      <Header size="small">
        <TranslationComponent stringKey={'Selezionare le misure adottate'} />
      </Header>

      <Grid>
        {filteredMisureEControlli.map((measure) =>
          [EMisureEControlli.matricolaAnalizzatore, EMisureEControlli.scadenzaCalibrazione].includes(measure) ? (
            renderMisureEControlliExtra(measure)
          ) : (
            <Grid.Row key={measure}>
              <Grid.Column>
                <Grid>
                  <Grid.Row>
                    <Grid.Column>
                      <InputLabel className={'label-misure-e-controlli'}>
                        <Field type="checkbox" name="misureEControlli" value={measure} />

                        <span>
                          <TranslationComponent stringKey={measure} shouldUnescape />
                        </span>
                      </InputLabel>
                    </Grid.Column>
                  </Grid.Row>
                </Grid>

                {renderMisureEControlliExtra(measure)}
              </Grid.Column>
            </Grid.Row>
          )
        )}

        <Grid.Row>
          <Grid.Column>
            <ErrorMessage name={'misureEControlli'}>
              {(errorMessage) => <Message error visible content={errorMessage} />}
            </ErrorMessage>
          </Grid.Column>
        </Grid.Row>
      </Grid>
    </BorderedDiv>
  );
};
